import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useInView, animate } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1552072232-059f1a5d76c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZSUyMGJsdWV8ZW58MHx8fHwxNzg3OTk2MDYyfDA&ixlib=rb-4.1.0&q=85";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const Counter = ({ value }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const mv = useMotionValue(0);
    const text = useTransform(mv, (v) => String(Math.round(v)));

    useEffect(() => {
        if (!inView) return;
        const controls = animate(mv, value, { duration: 1.8, ease: EASE });
        return () => controls.stop();
    }, [inView, value, mv]);

    return <motion.span ref={ref}>{text}</motion.span>;
};

const STATS = [
    { label: "Pillars", value: 6, caption: "Culture areas measured" },
    { label: "Signals", value: 4, caption: "Feed every culture score" },
    { label: "Roles", value: 4, caption: "Admins → team members" },
    { label: "Tenants", value: null, caption: "Unlimited organisations, fully separated" },
];

export const Hero = ({ onNavigate }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-screen items-center overflow-hidden bg-[#0A1B3F] text-white"
        >
            <motion.div style={{ y: imgY }} className="absolute inset-0">
                <img src={HERO_IMG} alt="" className="h-[115%] w-full object-cover opacity-20" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A1B3F]/70 via-[#0A1B3F]/40 to-[#0A1B3F]" />
            </motion.div>
            <div className="pointer-events-none absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#1E3A8A] opacity-70 blur-[140px]" />
            <div className="pointer-events-none absolute bottom-[-120px] right-[10%] h-[360px] w-[360px] rounded-full bg-[#1FA187] opacity-25 blur-[160px]" />

            <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-36 lg:px-10 lg:pt-40">
                <motion.p
                    data-testid="hero-overline"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="mb-8 flex items-center gap-4 text-xs font-bold uppercase tracking-[0.3em] text-[#7FD8C4]"
                >
                    <span className="h-px w-10 bg-[#7FD8C4]" />
                    Engineering culture
                </motion.p>

                <h1 className="font-display max-w-6xl text-5xl font-bold leading-[0.98] tracking-tight sm:text-7xl lg:text-[6.2vw]">
                    <MaskedLine delay={0.15}>Build a workplace</MaskedLine>
                    <MaskedLine delay={0.32}>worth showing up for.</MaskedLine>
                </h1>

                <div className="mt-14 grid gap-14 lg:grid-cols-12 lg:gap-8">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
                        className="lg:col-span-7"
                    >
                        <p
                            data-testid="hero-subcopy"
                            className="max-w-xl text-base leading-relaxed text-slate-300 lg:text-lg"
                        >
                            EML Synergy turns leadership intent into measurable behaviour. Assign focused
                            training, capture the challenges your team hits in the real world, and see culture
                            strengthen — pillar by pillar, week by week.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <button
                                data-testid="hero-request-demo-btn"
                                onClick={() => onNavigate("#contact")}
                                className="group inline-flex items-center gap-2 bg-white px-7 py-4 text-sm font-semibold text-[#0A1B3F] transition-colors duration-300 hover:bg-[#7FD8C4]"
                            >
                                Request a demo
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                            </button>
                            <button
                                data-testid="hero-explore-platform-btn"
                                onClick={() => onNavigate("#platform")}
                                className="group inline-flex items-center gap-2 border border-white/25 px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:border-white hover:bg-white/5"
                            >
                                Explore the platform
                                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                            </button>
                        </div>
                    </motion.div>

                    <motion.dl
                        data-testid="hero-stats"
                        initial="hidden"
                        animate="visible"
                        variants={{ visible: { transition: { staggerChildren: 0.12, delayChildren: 0.75 } } }}
                        className="grid grid-cols-2 content-center gap-x-8 gap-y-12 lg:col-span-5"
                    >
                        {STATS.map((s) => (
                            <motion.div
                                key={s.label}
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
                                }}
                            >
                                <dt className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#7FD8C4]">
                                    {s.label}
                                </dt>
                                <dd className="font-display mt-2 text-5xl font-bold lg:text-6xl">
                                    {s.value === null ? "∞" : <Counter value={s.value} />}
                                </dd>
                                <p className="mt-2 text-sm text-slate-400">{s.caption}</p>
                            </motion.div>
                        ))}
                    </motion.dl>
                </div>
            </div>
        </section>
    );
};
